import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useRouteMatch } from "react-router-dom";
import { getLenderConfig } from "../actions/lender.action";
import { logout } from "../actions/login.action";
import { AdminDetails } from "../dto";
import { useEnableDashboardStats } from "../hooks/useEnableDashbaordStats";
import { ReactComponent as BorrowerIcon } from "../images/business.svg";
import { ReactComponent as DashboardIcon } from "../images/dashboard.svg";
import { ReactComponent as ApplicationsIcon } from "../images/enquiries.svg";
import { ReactComponent as ListCheckIcon } from "../images/list-checks.svg";
import { ReactComponent as LMSLogo } from "../images/lms-logo.svg";
import { ReactComponent as LoansIcon } from "../images/loan.svg";
import { ReactComponent as LogoutIcon } from "../images/logout.svg";
import { ReactComponent as PadLockIcon } from "../images/pad-lock.svg";
import { NavItemProps, RoutePaths } from "../models";
import { ReduxState } from "../reducers";
import "./styles.css";

const ACCESS_CONTROL_ROLES = ["ADMIN", "SUPER_ADMIN"];

const navItems: NavItemProps[] = [
  {
    name: "Dashboard",
    path: RoutePaths.DASHBOARD,
    exact: true,
    Icon: DashboardIcon,
  },
  {
    name: "Borrowers",
    path: RoutePaths.BORROWERS,
    Icon: BorrowerIcon,
  },

  {
    name: "Applications",
    path: RoutePaths.APPLICATIONS,
    Icon: ApplicationsIcon,
  },
  {
    name: "Personal Loans",
    path: RoutePaths.PERSONAL_LOANS,
    Icon: LoansIcon,
  },
  {
    name: "Enterprise Loans",
    path: RoutePaths.ENTERPRISE_LOANS,
    Icon: LoansIcon,
  },
  // INFO: Hiding Reports section as per business requirement
  // {
  //   name: "Reports",
  //   path: RoutePaths.REPORTS,
  //   Icon: ClipBoardIcon,
  // },
  {
    name: "Agreements",
    path: RoutePaths.AGREEMENTS,
    Icon: ListCheckIcon,
  },
  // TODO: Commenting the code for now will undo the change on 24 Aug
  // {
  //   name: "Invoice",
  //   path: RoutePaths.INVOICE,
  //   Icon: InvoiceIcon,
  // },
  // INFO: Commenting the code for the business requirement
  // {
  //   name: "Portfolio",
  //   path: RoutePaths.PORTFOLIO,
  //   Icon: InvoiceIcon,
  // },
  {
    name: "Employers",
    path: RoutePaths.EMPLOYERS,
    Icon: BorrowerIcon,
  },
  {
    name: "Portal Access",
    path: RoutePaths.PORTAL_ACCESS,
    Icon: PadLockIcon,
  },
  {
    name: "Credit Decision",
    path: RoutePaths.CREDIT_DECISION,
    Icon: ListCheckIcon,
    dropdown: [
      {
        name: "Policy Attachments",
        path: RoutePaths.UNDERWRITING_POLICY_ATTACHMENTS,
      },
      {
        name: "Policy Simulations",
        path: RoutePaths.CREDIT_DECISION_POLICY_SIMULATIONS,
      },
      {
        name: "Policies",
        path: RoutePaths.UNDERWRITING_UI_POLICIES,
      },
      {
        name: "Audit Logs",
        path: RoutePaths.CREDIT_DECISION_AUDIT_LOGS,
      },
      {
        name: "Pre-Approvals",
        path: RoutePaths.CREDIT_DECISION_PRE_APPROVALS,
      },
      {
        name: "Pricing",
        path: RoutePaths.CREDIT_DECISION_PRICING,
      },
      {
        name: "Manual Review",
        path: RoutePaths.CREDIT_DECISION_MANUAL_REVIEW,
      },
    ],
  },
];

function NavItem({ name, path, exact = false, Icon, dropdown }: NavItemProps) {
  const routeMatch = useRouteMatch(path);
  const [isDropdownActive, setIsDropdownActive] = useState(false);

  useEffect(() => {
    if (routeMatch !== null) {
      setIsDropdownActive(true);
    }
  }, [routeMatch]);

  if (dropdown) {
    return (
      <li className="sidenav-link">
        <button
          className="btn btn-link d-flex flex-row align-items-center dropdown-toggle"
          aria-expanded={isDropdownActive}
          onClick={() => setIsDropdownActive((prevState) => !prevState)}
        >
          {Icon && <Icon className="mr-3" />}
          <span className="d-none d-md-block">{name}</span>
        </button>
        <ul
          className={`list-unstyled mt-1 pl-2 collapse${
            isDropdownActive ? " show" : ""
          }`}
        >
          {dropdown.map((navItem, index) => (
            <NavItem key={index} {...navItem} />
          ))}
        </ul>
      </li>
    );
  }
  return (
    <li className="sidenav-link">
      <NavLink
        to={path}
        className="d-flex flex-row align-items-center"
        exact={exact}
        activeClassName="link-active"
      >
        {Icon && <Icon className="mr-3" />}
        <span className="d-none d-md-block">{name}</span>
      </NavLink>
    </li>
  );
}

export default function SideNav() {
  const storeDispatch = useDispatch();
  const lenderId = useSelector(
    (state: ReduxState) => state.admin.data?.lenderId,
  );
  const admin: AdminDetails | null = useSelector(
    (state: ReduxState) => state.admin.data,
  );

  const { isEnabled: shouldShowDashboardNavItem } = useEnableDashboardStats({
    action: getLenderConfig(lenderId),
  });

  const filteredNavItems = useMemo(() => {
    const adminRoles = admin?.roles || [];
    const hasAccessControlRole = adminRoles.some((role) =>
      ACCESS_CONTROL_ROLES.includes(role),
    );

    return navItems.filter((item) => {
      if (item.name === "Dashboard") {
        return shouldShowDashboardNavItem;
      }
      if (item.name === "Portal Access") {
        return hasAccessControlRole;
      }
      return true;
    });
  }, [admin, shouldShowDashboardNavItem]);

  return (
    <nav className={`sidenav d-flex flex-column justify-content-between`}>
      <div className="sidenav-top">
        <div className="sidenav-brand">
          <LMSLogo />
        </div>
        <ul className="list-unstyled sidenav-links">
          {filteredNavItems.map((navItem, index) => (
            <NavItem key={index} {...navItem} />
          ))}
        </ul>
      </div>
      <div className="sidenav-bottom">
        <div className="sidenav-link">
          <button
            className="btn btn-link d-flex flex-row align-items-center"
            onClick={() => logout(storeDispatch)}
          >
            <LogoutIcon className="mr-3" />
            <span className="d-none d-md-block">Logout</span>
          </button>
        </div>
      </div>
    </nav>
  );
}
